import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Paper, Chip, Table, TableHead, TableRow, TableCell,
  TableBody, Button, CircularProgress, IconButton, Tooltip, Stack,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { qcStockService } from '../services/api';
import { format, parseISO } from 'date-fns';
import { uk } from 'date-fns/locale';

const STATUS_META = {
  on_hold: { label: 'На перевірці', color: 'warning' },
  approved: { label: 'Затверджено', color: 'success' },
  rejected: { label: 'Відхилено', color: 'error' },
  returned: { label: 'Повернуто у виробництво', color: 'info' },
};

function fmtDate(val) {
  if (!val) return '—';
  try { return format(parseISO(val), 'dd.MM.yyyy HH:mm', { locale: uk }); }
  catch { return val; }
}

export default function QCStockPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [resolving, setResolving] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await qcStockService.getAll();
      setItems(res.data.results || res.data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function resolve(id, status) {
    setResolving(id + status);
    try {
      await qcStockService.resolve(id, { status });
      load();
    } catch (e) {
      alert(e?.response?.data?.detail || 'Помилка');
    } finally {
      setResolving(null);
    }
  }

  const counts = { on_hold: 0, approved: 0, rejected: 0, returned: 0 };
  items.forEach(i => { if (counts[i.status] !== undefined) counts[i.status]++; });

  return (
    <Box p={2}>
      <Box display="flex" alignItems="center" mb={2} gap={1}>
        <Typography variant="h5" fontWeight="bold">Склад контролю якості</Typography>
        <Tooltip title="Оновити">
          <IconButton onClick={load} size="small"><RefreshIcon /></IconButton>
        </Tooltip>
        {loading && <CircularProgress size={20} />}
      </Box>

      <Stack direction="row" spacing={2} mb={3} flexWrap="wrap">
        {Object.entries(STATUS_META).map(([key, meta]) => (
          <Paper key={key} sx={{ p: 1.5, minWidth: 140, textAlign: 'center' }}>
            <Typography variant="h4" fontWeight="bold">{counts[key]}</Typography>
            <Typography variant="body2" color="text.secondary">{meta.label}</Typography>
          </Paper>
        ))}
      </Stack>

      <Paper>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Виріб</TableCell>
              <TableCell>Замовлення / Верстат</TableCell>
              <TableCell align="right">К-сть</TableCell>
              <TableCell>Статус</TableCell>
              <TableCell>Прийнято</TableCell>
              <TableCell>Дії</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.length === 0 && !loading && (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography color="text.secondary" py={2}>Немає записів</Typography>
                </TableCell>
              </TableRow>
            )}
            {items.map(item => {
              const meta = STATUS_META[item.status] || { label: item.status_display, color: 'default' };
              return (
                <TableRow key={item.id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight="bold">{item.product_name}</Typography>
                    {item.product_article && (
                      <Typography variant="caption" color="text.secondary">{item.product_article}</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{item.order_number || '—'}</Typography>
                    <Typography variant="caption" color="text.secondary">{item.order_machine}</Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography fontWeight="bold">{item.quantity} шт</Typography>
                  </TableCell>
                  <TableCell>
                    <Chip label={meta.label} color={meta.color} size="small" />
                  </TableCell>
                  <TableCell>{fmtDate(item.received_at)}</TableCell>
                  <TableCell>
                    {item.status === 'on_hold' && (
                      <Stack direction="row" spacing={0.5}>
                        <Button
                          size="small" variant="contained" color="success"
                          disabled={!!resolving}
                          onClick={() => resolve(item.id, 'approved')}
                        >
                          {resolving === item.id + 'approved' ? <CircularProgress size={14} /> : 'Затвердити'}
                        </Button>
                        <Button
                          size="small" variant="contained" color="error"
                          disabled={!!resolving}
                          onClick={() => resolve(item.id, 'rejected')}
                        >
                          {resolving === item.id + 'rejected' ? <CircularProgress size={14} /> : 'Відхилити'}
                        </Button>
                        <Button
                          size="small" variant="outlined"
                          disabled={!!resolving}
                          onClick={() => resolve(item.id, 'returned')}
                        >
                          {resolving === item.id + 'returned' ? <CircularProgress size={14} /> : 'Повернути'}
                        </Button>
                      </Stack>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Paper>
    </Box>
  );
}
